import { ColumnDef, TableState } from '@tanstack/react-table';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { ColumnResizingContextProvider } from 'src/contexts/ColumnResizingContext/ColumnResizingContext';
import {
  Content,
  FormatContent,
  useContent,
} from 'src/contexts/ContentContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { useSeatingColumnSorting } from 'src/tables/common/hooks/useSeatingColumnSorting';
import { defaultRecentSalesV2Columns } from 'src/tables/SalesTable/configs/RecentSaleTableColumnsConfig';
import { RECENT_SALES_TABLE_COLUMNS_CONFIG_V3 } from 'src/tables/SalesTable/configs/RecentSaleTableV3ColumnsConfig';
import { SaleWithEvent } from 'src/tables/SalesTable/SalesTable.type';
import { NoData } from 'src/tables/Table';
import { RowWrapper } from 'src/tables/Table/Table.types';
import { Table } from 'src/tables/TableV2/Table';
import { createNumericColorGradientThresholds } from 'src/utils/cellUtils';
import { getRecentSalesColumnConfigById } from 'src/utils/columns/columnUtils';
import {
  RecentSalesTableColumnId,
  SalesTableColumnId,
} from 'src/utils/columns/sales/salesColumnUtils.types';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { getEventPerformerVenue } from 'src/utils/eventWithDataUtils';
import { SectionType } from 'src/utils/types/sectionType';
import { Feature, Sale, UserSetting } from 'src/WebApiController';

const PAGE_SIZE = 10;

export const RecentSalesTableV3 = ({
  data,
  onRowWrapper,
}: {
  data: Sale[];
  onRowWrapper: RowWrapper<SaleWithEvent | null>;
}) => {
  const { data: catalog, isLoading: isCatalogLoading } =
    useCatalogDataContext();

  const salesText = useContent(ContentId.Sales);
  const { isDarkMode } = useSiteTheme();
  const hasPurchasedByColumnFeature = useUserHasFeature(
    Feature.RecentSalesV2PurchasedByColumn
  );

  const seatingColumnSorting = useSeatingColumnSorting<SaleWithEvent>(
    SalesTableColumnId.Seating,
    SectionType.Sales
  );

  const filteredData = useMemo<(SaleWithEvent | null)[]>(
    () =>
      data
        .map((sale) => {
          const { event, performer, venue } = getEventPerformerVenue(
            sale.viagVirtId,
            catalog
          );

          if (event) {
            return {
              event: event.event,
              sale,
              performer,
              venue,
            } as SaleWithEvent;
          }

          return null;
        })
        ?.filter((s) => s != null)
        ?.map((s) => s!),
    [catalog, data]
  );

  const defaultColumns = useMemo(
    () =>
      hasPurchasedByColumnFeature
        ? defaultRecentSalesV2Columns
        : defaultRecentSalesV2Columns.filter(
            (col) => col !== RecentSalesTableColumnId.PurchasedBy
          ),
    [hasPurchasedByColumnFeature]
  );

  const {
    value: storedRecentSalesTableColumnOrderSetting = defaultColumns,
    setUserSetting: setRecentSalesTableColumnsOrderSetting,
  } = useServerUserSetting<string[]>({
    id: UserSetting.RecentSalesTableColumnsOrder,
  });

  const { value: storedRecentSalesTableColumnsEnabledSetting } =
    useServerUserSetting<string[]>({
      id: UserSetting.RecentSalesTableColumnsEnabled,
    });

  const displayedColumnsConfig = useMemo(() => {
    const saleColumnOrder = storedRecentSalesTableColumnOrderSetting
      .filter(
        // If storedSaleColumnsEnabledSetting has never been set, default to true
        (c) => storedRecentSalesTableColumnsEnabledSetting?.includes(c) ?? true
      )
      .filter(
        // Filter out PurchasedBy column if feature flag is disabled
        (c) =>
          hasPurchasedByColumnFeature ||
          c !== RecentSalesTableColumnId.PurchasedBy
      );

    const salesTableColumnsConfig = Object.values(
      RECENT_SALES_TABLE_COLUMNS_CONFIG_V3
    );

    const columns = new Set([
      ...salesTableColumnsConfig
        .filter((column) => {
          const columnDef = getRecentSalesColumnConfigById(
            column.id as SalesTableColumnId
          ).personalization;
          return !columnDef.isConfigurable;
        })
        .map((item) => item.id as RecentSalesTableColumnId),
      ...saleColumnOrder,
    ]);

    return Array.from(columns).reduce<ColumnDef<SaleWithEvent>[]>(
      (acc, columnId) => {
        // Filter out PurchasedBy column if feature flag is disabled
        if (
          !hasPurchasedByColumnFeature &&
          columnId === RecentSalesTableColumnId.PurchasedBy
        ) {
          return acc;
        }
        const columnConfig = salesTableColumnsConfig.find(
          (column) => column.id === columnId
        );
        if (columnConfig) acc.push(columnConfig as ColumnDef<SaleWithEvent>);
        return acc;
      },
      []
    );
  }, [
    storedRecentSalesTableColumnOrderSetting,
    storedRecentSalesTableColumnsEnabledSetting,
    hasPurchasedByColumnFeature,
  ]);

  // Calculate thresholds for P&L and Margin columns
  const numericColorThresholds = useMemo(() => {
    const pandlValues = filteredData
      .map((d) => d?.sale?.pandL?.amt)
      .filter((val): val is number => val != null);

    const marginValues = filteredData
      .map((d) => d?.sale?.margin)
      .filter((val): val is number => val != null);

    return {
      [RecentSalesTableColumnId.PandL]:
        createNumericColorGradientThresholds(pandlValues),
      [RecentSalesTableColumnId.Margin]:
        createNumericColorGradientThresholds(marginValues),
    };
  }, [filteredData]);

  if (isCatalogLoading) {
    return <PosSpinner />;
  }

  if (!filteredData.length) {
    return (
      <NoData>
        <FormatContent
          id={FormatContentId.NoDataAvailable}
          params={salesText}
        />
      </NoData>
    );
  }

  return (
    <ColumnResizingContextProvider
      userSettingId={UserSetting.RecentSalesTableColumnsWidths}
    >
      <Table<SaleWithEvent>
        actionColId={RecentSalesTableColumnId.Actions}
        data={filteredData as SaleWithEvent[]}
        columns={displayedColumnsConfig}
        pagination={{
          pageSize: PAGE_SIZE,
        }}
        sortingFns={{ ...seatingColumnSorting }}
        meta={{
          sectionType: SectionType.RecentSalesTable,
          numericColorThresholds,
        }}
        rowWrapper={onRowWrapper}
        footerCta={
          <Stack style={{ marginTop: vars.spacing.xl }}>
            <Link to="/sales">
              <Button variant="outline">
                <Content id={ContentId.ViewAllSales} />
              </Button>
            </Link>
          </Stack>
        }
      />
    </ColumnResizingContextProvider>
  );
};
